import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Loader2 } from "lucide-react";
import { REVEAL, PHONE_DISPLAY, PHONE_TEL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUEL_OPTIONS = [
  "Petrol in a Diesel car",
  "Diesel in a Petrol car",
  "Not sure / Something else",
];

const initial = { name: "", phone: "", location: "", car: "", fuel_type: FUEL_OPTIONS[0], message: "" };

export const RequestForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.location.trim()) {
      toast.error("Please add your name, phone and location so we can find you.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/fuel-requests`, form);
      toast.success("Help is on the way! A Fuel Hero specialist will call you shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly and we'll sort it.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-black/15 bg-white p-3.5 font-medium text-[#171717] placeholder-[#171717]/35 focus:outline-none focus:border-[#1F7A6B] focus:ring-2 focus:ring-[#1F7A6B]/20 transition-colors";
  const labelCls = "mb-1.5 block text-sm font-semibold text-[#171717]/70";

  return (
    <section id="request" data-testid="request-section" className="border-b border-black/10 bg-[#22303C] py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div variants={REVEAL} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }}>
          <span className="kicker text-[#E7DFCF]">Request a rescue</span>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl leading-tight text-white">
            Tell us where you are
          </h2>
          <p className="mt-4 max-w-md text-lg leading-relaxed text-white/70">
            Fill this out and a specialist calls you back fast. In a hurry? Skip the form and call the hotline now.
          </p>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="request-call-button"
            className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-[#1F7A6B] px-7 py-4 text-lg font-semibold text-white hover:bg-[#185f54] transition-colors"
          >
            <Phone strokeWidth={2.5} className="h-5 w-5" /> {PHONE_DISPLAY}
          </a>
        </motion.div>

        <motion.form
          onSubmit={submit}
          variants={REVEAL}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="rounded-2xl border border-black/10 bg-white p-6 md:p-8 shadow-soft"
          data-testid="fuel-request-form"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Your name*</label>
              <input data-testid="input-name" className={inputCls} value={form.name} onChange={update("name")} placeholder="Jane Driver" />
            </div>
            <div>
              <label className={labelCls}>Phone*</label>
              <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={update("phone")} placeholder="0400 000 000" />
            </div>
          </div>

          <div className="mt-4">
            <label className={labelCls}>Where are you?*</label>
            <input data-testid="input-location" className={inputCls} value={form.location} onChange={update("location")} placeholder="Servo / street address / suburb" />
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Car (optional)</label>
              <input data-testid="input-car" className={inputCls} value={form.car} onChange={update("car")} placeholder="e.g. Toyota Hilux 2021" />
            </div>
            <div>
              <label className={labelCls}>What happened?</label>
              <select data-testid="input-fuel-type" className={inputCls} value={form.fuel_type} onChange={update("fuel_type")}>
                {FUEL_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className={labelCls}>Extra details (optional)</label>
            <textarea data-testid="input-message" rows={3} className={inputCls} value={form.message} onChange={update("message")} placeholder="Anything else we should know?" />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="submit-request-button"
            className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-[#1F7A6B] px-6 py-4 text-lg font-semibold text-white hover:bg-[#185f54] transition-colors disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send strokeWidth={2.5} className="h-5 w-5" />}
            {loading ? "Sending..." : "Send rescue request"}
          </button>
          <p className="mt-3 text-center text-sm text-[#171717]/50">No fix, no fee. We'll never charge a call-out just to look.</p>
        </motion.form>
      </div>
    </section>
  );
};
